import { useState } from 'react';

// Generic category donut — a few slices with explicit colours, a legend with
// value + share, and a hover that lifts a slice while the centre shows its
// figure. Domain-agnostic: pass a `format` for legend values and an optional
// `formatShort` for the compact centre figure (defaults to `format`). Finances
// wraps this with euro formatters; Account passes a plain count formatter.
const CX = 50, CY = 50, R = 40, RI = 24;

function arc(a: number, r: number): [number, number] {
  return [CX + r * Math.cos(a), CY + r * Math.sin(a)];
}
function slicePath(a0: number, a1: number): string {
  const large = a1 - a0 > Math.PI ? 1 : 0;
  const [ox0, oy0] = arc(a0, R);
  const [ox1, oy1] = arc(a1, R);
  const [ix1, iy1] = arc(a1, RI);
  const [ix0, iy0] = arc(a0, RI);
  return `M${ox0} ${oy0} A${R} ${R} 0 ${large} 1 ${ox1} ${oy1} L${ix1} ${iy1} A${RI} ${RI} 0 ${large} 0 ${ix0} ${iy0} Z`;
}

export interface DonutSeg { label: string; value: number; color: string }

export interface DonutProps {
  title: string;
  segments: DonutSeg[];
  // Legend value formatter (full). Defaults to a plain integer.
  format?: (n: number) => string;
  // Compact centre formatter. Defaults to `format`.
  formatShort?: (n: number) => string;
  // Centre caption when no slice is hovered. Defaults to 'total'.
  centerLabel?: string;
  // Render the visible title row. Off when an outer card head already names the
  // chart (the title is still the SVG's accessible label). Defaults to true.
  showTitle?: boolean;
}

export function Donut({ title, segments, format = (n) => String(n), formatShort, centerLabel = 'total', showTitle = true }: DonutProps) {
  const short = formatShort ?? format;
  const [hi, setHi] = useState<number | null>(null);
  const total = segments.reduce((a, s) => a + s.value, 0);
  let acc = -Math.PI / 2;
  const slices = segments.map((s, i) => {
    const a0 = acc;
    const a1 = acc + (total ? (s.value / total) * Math.PI * 2 : 0);
    acc = a1;
    return { s, i, a0, a1 };
  });
  const focus = hi != null ? segments[hi]! : null;

  return (
    <div className="fin-donut">
      {showTitle && title && <div className="fin-donut-title">{title}</div>}
      <div className="fin-donut-body">
        <div className="fin-donut-chart" onMouseLeave={() => setHi(null)}>
          <svg viewBox="0 0 100 100" role="img" aria-label={title}>
            {slices.map((sl) => (
              <path
                key={sl.i}
                d={slicePath(sl.a0, sl.a1)}
                fill={sl.s.color}
                stroke="var(--surface)"
                strokeWidth="0.8"
                opacity={hi == null || hi === sl.i ? 1 : 0.5}
                onMouseEnter={() => setHi(sl.i)}
              />
            ))}
            <text x={CX} y={CY - 1} textAnchor="middle" className="fin-donut-cval">{focus ? short(focus.value) : short(total)}</text>
            <text x={CX} y={CY + 7} textAnchor="middle" className="fin-donut-clbl">{focus ? `${focus.label} · ${total ? Math.round((focus.value / total) * 100) : 0}%` : centerLabel}</text>
          </svg>
        </div>
        <div className="fin-donut-legend">
          {segments.map((s) => (
            <div className="fin-donut-leg" key={s.label}>
              <span className="lbl"><span className="sw" style={{ background: s.color }} />{s.label}</span>
              <span className="mono val">{format(s.value)}</span>
              <span className="mono pct">{total ? Math.round((s.value / total) * 100) : 0}%</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
